import 'core-js/modules/es6.object.keys.js';
import 'core-js/modules/es6.symbol.js';
import 'core-js/modules/es6.array.filter.js';
import 'core-js/modules/es6.object.get-own-property-descriptor.js';
import 'core-js/modules/es7.object.get-own-property-descriptors.js';
import _extends from '@babel/runtime/helpers/esm/extends';
import _defineProperty from '@babel/runtime/helpers/esm/defineProperty';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import React__default, { useState, useRef, useEffect, forwardRef } from 'react';
import { useMountedState } from 'react-use';
import { isIE11 } from '../../utils/isIE11.js';
import { createPortal } from 'react-dom';
import { Classes } from '@blueprintjs/core';
import { usePopper } from 'react-popper';
import cx from 'classnames';
import { Arrow, TooltipArrow } from './arrow.js';
import useCombinedRefs from '../../utils/use-combined-refs.js';
import { useCallbackRef } from '@radix-ui/react-use-callback-ref';

var _excluded = ["open", "defaultOpen", "onOpenChange", "className", "arrowType", "content", "triggerRef", "contentRef", "contentWrapperProps", "portaled", "offset"],
    _excluded2 = ["arrowType", "popperStyles", "children", "setArrowElement", "arrowStyles", "placement", "style", "portaled"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

/**
 * IE11 requires this state shim to fix a bug in IE11.
 */
var usePopoverIsOpenShim = function usePopoverIsOpenShim(existingArgs) {
  var _ref, _existingArgs$open;

  var _useState = useState((_ref = (_existingArgs$open = existingArgs === null || existingArgs === void 0 ? void 0 : existingArgs.open) !== null && _existingArgs$open !== void 0 ? _existingArgs$open : existingArgs === null || existingArgs === void 0 ? void 0 : existingArgs.defaultOpen) !== null && _ref !== void 0 ? _ref : false),
      _useState2 = _slicedToArray(_useState, 2),
      open = _useState2[0],
      setOpen = _useState2[1];

  var setLocalIsOpenCalledRecent = useRef(false);
  var getIsMounted = useMountedState();
  var onOpenChange = useCallbackRef(function (newOpen, triggeredExternally) {
    if (!setLocalIsOpenCalledRecent.current && getIsMounted()) {
      setOpen(newOpen);

      if (!triggeredExternally) {
        var _existingArgs$onOpenC;

        existingArgs === null || existingArgs === void 0 ? void 0 : (_existingArgs$onOpenC = existingArgs.onOpenChange) === null || _existingArgs$onOpenC === void 0 ? void 0 : _existingArgs$onOpenC.call(existingArgs, newOpen);
      }
    }

    setLocalIsOpenCalledRecent.current = true;
    setTimeout(function () {
      setLocalIsOpenCalledRecent.current = false;
    }, 300);
  });
  useEffect(function () {
    if ((existingArgs === null || existingArgs === void 0 ? void 0 : existingArgs.open) !== undefined) {
      onOpenChange(existingArgs.open, true);
    }
  }, [existingArgs === null || existingArgs === void 0 ? void 0 : existingArgs.open]);

  if (!isIE11()) {
    return existingArgs !== null && existingArgs !== void 0 ? existingArgs : {};
  }

  return {
    open: open,
    onOpenChange: onOpenChange
  };
};
var usePopperJsPositioning = function usePopperJsPositioning(_ref2) {
  var open = _ref2.open,
      defaultOpen = _ref2.defaultOpen,
      onOpenChange = _ref2.onOpenChange,
      className = _ref2.className,
      arrowType = _ref2.arrowType,
      content = _ref2.content,
      triggerRef = _ref2.triggerRef,
      contentRef = _ref2.contentRef,
      contentWrapperProps = _ref2.contentWrapperProps,
      portaled = _ref2.portaled,
      _ref2$offset = _ref2.offset,
      offset = _ref2$offset === void 0 ? [0, 0] : _ref2$offset,
      otherProps = _objectWithoutProperties(_ref2, _excluded);

  var _useState3 = useState(null),
      _useState4 = _slicedToArray(_useState3, 2),
      referenceElement = _useState4[0],
      setReferenceElement = _useState4[1];

  var _useState5 = useState(null),
      _useState6 = _slicedToArray(_useState5, 2),
      popperElement = _useState6[0],
      setPopperElement = _useState6[1];

  var _useState7 = useState(null),
      _useState8 = _slicedToArray(_useState7, 2),
      arrowElement = _useState8[0],
      setArrowElement = _useState8[1];

  var _usePopper = usePopper(referenceElement, popperElement, {
    placement: otherProps.placement,
    modifiers: arrowType !== "none" ? [{
      name: "arrow",
      options: {
        element: arrowElement
      }
    }, {
      enabled: true,
      name: "offset",
      options: {
        // 30 / 2 is from blueprint source.
        offset: [0 + offset[0], 30 / 2 + offset[1]]
      }
    }] : [{
      name: "offset",
      options: {
        offset: [offset[0], offset[1]]
      }
    }]
  }),
      styles = _usePopper.styles,
      attributes = _usePopper.attributes,
      state = _usePopper.state;

  var rootProps = usePopoverIsOpenShim({
    defaultOpen: defaultOpen,
    open: open,
    onOpenChange: onOpenChange
  });
  var combinedTriggerRef = useCombinedRefs(setReferenceElement, triggerRef);
  var combineContentRef = useCombinedRefs(setPopperElement, contentRef);
  return {
    rootProps: rootProps,
    triggerProps: {
      ref: combinedTriggerRef,
      asChild: true
    },
    contentProps: _objectSpread(_objectSpread({
      ref: combineContentRef,
      className: cx(className, Classes.POPOVER),
      asChild: true,
      // this is a fun hack to avoid some weird positioning issues
      portalled: false
    }, otherProps), {}, {
      children: /*#__PURE__*/React__default.createElement(PopoverContent, _extends({
        arrowType: arrowType
      }, contentWrapperProps, {
        popperStyles: styles.popper
      }, attributes.popper, {
        setArrowElement: arrowType !== "none" ? setArrowElement : undefined,
        arrowStyles: styles.arrow,
        placement: state === null || state === void 0 ? void 0 : state.placement,
        portaled: portaled
      }), content !== null && content !== void 0 ? content : null)
    }),
    popperResults: {
      styles: styles,
      attributes: attributes
    },
    setArrowElement: setArrowElement
  };
};
var PopoverContent = /*#__PURE__*/forwardRef(function (_ref3, ref) {
  var arrowType = _ref3.arrowType,
      popperStyles = _ref3.popperStyles,
      children = _ref3.children,
      setArrowElement = _ref3.setArrowElement,
      arrowStyles = _ref3.arrowStyles,
      placement = _ref3.placement,
      style = _ref3.style,
      _ref3$portaled = _ref3.portaled,
      portaled = _ref3$portaled === void 0 ? true : _ref3$portaled,
      props = _objectWithoutProperties(_ref3, _excluded2);

  /**
   * Slight edge case for tooltips: Tooltips try to clone content in order to hide it for accessibility.
   * We detect that case via the className which should be undefined.
   */
  if (props.className === undefined) {
    return /*#__PURE__*/React__default.createElement("span", _extends({
      ref: ref
    }, props), children);
  }

  var content = /*#__PURE__*/React__default.createElement("div", _extends({
    ref: ref
  }, props, {
    style: _objectSpread(_objectSpread({}, style), popperStyles)
  }), setArrowElement && arrowType === "default" && /*#__PURE__*/React__default.createElement(Arrow, {
    ref: setArrowElement,
    style: arrowStyles,
    placement: placement
  }), setArrowElement && arrowType === "tooltip" && /*#__PURE__*/React__default.createElement(TooltipArrow, {
    ref: setArrowElement,
    style: arrowStyles,
    placement: placement
  }), /*#__PURE__*/React__default.createElement("div", {
    className: Classes.POPOVER_CONTENT
  }, children));

  if (!portaled) {
    return content;
  }

  return /*#__PURE__*/createPortal(content, document.body);
});

export { PopoverContent, usePopoverIsOpenShim, usePopperJsPositioning };
//# sourceMappingURL=utils.js.map
